import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface ZohoFieldMapping {
  id: string;
  system_field: string;
  system_field_label: string;
  zoho_field: string;
  zoho_field_label: string;
  field_type: string;
  is_required: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export function useZohoFieldMappings() {
  const [mappings, setMappings] = useState<ZohoFieldMapping[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadMappings = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('zoho_field_mappings')
        .select('*')
        .order('system_field_label');

      if (error) throw error;
      setMappings(data || []);
    } catch (err) {
      setError(err as Error);
      toast.error('Erro ao carregar mapeamentos');
    } finally {
      setLoading(false);
    }
  };

  const createMapping = async (mapping: Omit<ZohoFieldMapping, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const { data, error } = await supabase
        .from('zoho_field_mappings')
        .insert(mapping)
        .select()
        .single();

      if (error) throw error;
      
      toast.success('Mapeamento criado com sucesso!');
      await loadMappings();
      return data;
    } catch (err) {
      toast.error('Erro ao criar mapeamento');
      throw err;
    }
  };

  const updateMapping = async (id: string, updates: Partial<ZohoFieldMapping>) => {
    try {
      const { error } = await supabase
        .from('zoho_field_mappings')
        .update(updates)
        .eq('id', id);

      if (error) throw error;
      
      toast.success('Mapeamento atualizado!');
      await loadMappings();
    } catch (err) {
      toast.error('Erro ao atualizar mapeamento');
      throw err;
    }
  };

  const deleteMapping = async (id: string) => {
    try {
      const { error } = await supabase
        .from('zoho_field_mappings')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      toast.success('Mapeamento removido!');
      await loadMappings();
    } catch (err) {
      toast.error('Erro ao remover mapeamento');
      throw err;
    }
  };

  const toggleActive = async (id: string, isActive: boolean) => {
    await updateMapping(id, { is_active: isActive });
  };

  useEffect(() => {
    loadMappings();
  }, []);

  return {
    mappings,
    loading,
    error,
    loadMappings,
    createMapping,
    updateMapping,
    deleteMapping,
    toggleActive
  };
}
